"use client"

import * as React from "react"

type OwnerEntry = {
  ownerId: string | null
  members: string[]
}

const ownersByPath = new Map<string, OwnerEntry>()
const listenersByPath = new Map<string, Set<() => void>>()

export function useRoutePaginationOwner(pathname: string, tableId: string) {
  const [isOwner, setIsOwner] = React.useState(() =>
    computeIsOwner(pathname, tableId)
  )

  React.useEffect(() => {
    register(pathname, tableId)
    setIsOwner(computeIsOwner(pathname, tableId))

    const listener = () => setIsOwner(computeIsOwner(pathname, tableId))
    const listeners = listenersByPath.get(pathname) ?? new Set<() => void>()
    listeners.add(listener)
    listenersByPath.set(pathname, listeners)

    return () => {
      const pathListeners = listenersByPath.get(pathname)
      if (pathListeners) {
        pathListeners.delete(listener)
        if (pathListeners.size === 0) listenersByPath.delete(pathname)
      }
      unregister(pathname, tableId)
    }
  }, [pathname, tableId])

  return isOwner
}

function computeIsOwner(pathname: string, tableId: string) {
  const entry = ownersByPath.get(pathname)
  return entry?.ownerId === tableId
}

function register(pathname: string, tableId: string) {
  const entry = ownersByPath.get(pathname) ?? { ownerId: null, members: [] }
  if (!entry.members.includes(tableId)) {
    entry.members.push(tableId)
  }
  if (!entry.ownerId) {
    entry.ownerId = tableId
  }
  ownersByPath.set(pathname, entry)
  notify(pathname)
}

function unregister(pathname: string, tableId: string) {
  const entry = ownersByPath.get(pathname)
  if (!entry) return

  entry.members = entry.members.filter((id) => id !== tableId)
  if (entry.ownerId === tableId) {
    entry.ownerId = entry.members[0] ?? null
  }

  if (entry.members.length === 0) {
    ownersByPath.delete(pathname)
  } else {
    ownersByPath.set(pathname, entry)
  }

  notify(pathname)
}

function notify(pathname: string) {
  const listeners = listenersByPath.get(pathname)
  if (!listeners) return
  listeners.forEach((listener) => listener())
}
